/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { TmDetailedError } from '@treasury/domain/shared';
import type { WireIsoLabels } from '@treasury/domain/wires';
import {
    CreditorsService,
    Wire,
    WireCreditor,
    WireIsoService,
    WiresService,
    WireTemplate,
    WireType,
} from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { camelCaseToTitle } from '@treasury/utils';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import {
    WireAction,
    WireWorkflow,
    WireWorkflowParameters,
} from '../../types/wire-workflow-parameters.type';
import '../partials/wire-template-confirm-step';
import '../partials/wire-template-create-step';
import '../partials/wire-template-edit-step';
import '../partials/wire-template-initiate-step';
import '../partials/wire-template-review-step';

enum Step {
    Initiate,
    Create,
    Edit,
    Review,
    Confirm,
}

export const tagName = 'wire-template-workflow-container';
@customElement(tagName)
export class WireTemplateWorkflowContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private wireService: WiresService;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @state()
    private wire = new Wire();

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private wireTemplate = new WireTemplate();

    @state()
    private action = '' as WireAction;

    @state()
    private workflow = '' as WireWorkflow;

    @state()
    private wireType = WireType.Domestic;

    @state()
    private wireTemplateId = '';

    @state()
    private wireError?: TmDetailedError;

    @state()
    private activeStep = Step.Initiate;

    @state()
    private creditorDetailOpen = false;

    private get isCreateNewTemplate() {
        return this.action === 'create' && this.wireTemplateId === 'new';
    }

    protected render() {
        return html`<tm-body class="h-full" .shadow=${false} .padding=${false}>
                ${this.loading ? this.renderBlockingLoader() : this.renderStep()}
            </tm-body>
            ${this.renderCreditorDetail()}`;
    }

    protected async firstUpdated() {
        await this.setParams();
        const validDefaultFrequency = await this.wireService.getValidDefaultFrequency();
        this.wire.frequency = validDefaultFrequency;
        this.wireIsoLabels = await this.wireIsoService.getLabels();

        if (this.wireTemplateId !== 'new') {
            this.wireTemplate = await this.wireService.getTemplate(Number(this.wireTemplateId));
        }

        if (this.action === 'edit') {
            this.activeStep = Step.Edit;
        }

        if (this.action === 'initiate') {
            this.wire.wireCompany = this.wireTemplate.wireCompany;
            this.wire.debitAccount = this.wireTemplate.debitAccount;
            this.wire.beneficiary = new WireCreditor(this.wireTemplate.beneficiary);
            this.wire.purpose = this.wireTemplate.purpose;
            this.wire.additionalInformation = this.wireTemplate.additionalInformation;
            this.wire.referenceBeneficiary = this.wireTemplate.referenceBeneficiary;
            this.wire.isInternational = this.wireTemplate.isInternational;
            // this.wire.wireTemplate = this.wireTemplate.toDto();
        }

        if (this.isCreateNewTemplate) {
            this.activeStep = Step.Create;
        }

        this.wireType = this.wireTemplate.isInternational
            ? WireType.International
            : WireType.Domestic;

        this.loading = false;
    }

    private async setParams() {
        const { params } = await this.navService.getRouteData<WireWorkflowParameters>();
        const { action, id } = params;
        this.action = action;
        this.wireTemplateId = id;
        this.workflow = 'template';
    }

    private renderBlockingLoader() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return nothing;
    }

    private renderStep() {
        switch (this.activeStep) {
            case Step.Create:
                this.headerService.configure({
                    title: 'Create Wire Template',
                    backAction: () => {
                        const route = `/wires`;
                        this.navService.navigate(route);
                    },
                    enableFdicSign: true,
                });
                return html`<wire-template-create-step
                    .wireTemplate=${this.wireTemplate}
                    .wireType=${this.wireType}
                    .action=${this.action}
                    .workflow=${this.workflow}
                    @review=${({ detail }: CustomEvent) => {
                        this.wireTemplate = detail;
                        this.activeStep = Step.Review;
                    }}
                ></wire-template-create-step>`;
            case Step.Initiate:
                this.headerService.configure({
                    title: 'Create Wire from Template',
                    subtitle: camelCaseToTitle(this.wireType),
                    backAction: () => {
                        const route =
                            this.workflow === 'template' ? `/wires/templates/initiate` : `/wires`;

                        this.navService.navigate(route);
                    },
                    enableFdicSign: true,
                });
                return html`<wire-template-initiate-step
                    .wire=${this.wire}
                    .wireTemplate=${this.wireTemplate}
                    .wireType=${this.wireType}
                    .action=${this.action}
                    .workflow=${this.workflow}
                    @view-creditor-detail=${({ detail }: CustomEvent) => {
                        if (detail) {
                            this.creditorDetailOpen = detail;
                        }
                    }}
                    @review=${({ detail }: CustomEvent) => {
                        this.wire = detail;
                        this.activeStep = Step.Review;
                    }}
                ></wire-template-initiate-step>`;
            case Step.Edit:
                this.headerService.configure({
                    title: 'Edit Template',
                    backAction: () => {
                        this.navService.navigate(`/wires/template/${this.wireTemplate.id}`);
                    },
                    enableFdicSign: true,
                });
                return html`<wire-template-edit-step
                    .wire=${this.wire}
                    .wireTemplate=${this.wireTemplate}
                    .wireType=${this.wireType}
                    .action=${this.action}
                    .workflow=${this.workflow}
                    @review=${({ detail }: CustomEvent) => {
                        this.wireTemplate = detail;
                        this.activeStep = Step.Review;
                    }}
                ></wire-template-edit-step>`;
            case Step.Review:
                this.headerService.configure({
                    title: 'Review Wire',
                    backAction: () => {
                        this.activeStep = this.action === 'initiate' ? Step.Initiate : Step.Edit;
                    },
                    enableFdicSign: true,
                });
                return html`<wire-template-review-step
                    .wire=${this.wire}
                    .wireTemplate=${this.wireTemplate}
                    .action=${this.action}
                    @view-creditor-detail=${({ detail }: CustomEvent) => {
                        if (detail) {
                            this.creditorDetailOpen = detail;
                        }
                    }}
                    @confirm=${() => {
                        if (this.isCreateNewTemplate) {
                            return this.createWireTemplate();
                        }
                        return this.action === 'initiate'
                            ? this.createWire()
                            : this.updateWireTemplate();
                    }}
                ></wire-template-review-step>`;
            case Step.Confirm:
                this.headerService.configure({ title: 'Wire Confirmation' });
                return html`<wire-template-confirm-step
                    .wire=${this.wire}
                    .wireTemplate=${this.wireTemplate}
                    .action=${this.action}
                    @create-wire=${() => {
                        this.navService.navigate(`/wires/domestic/select/new/usd`);
                    }}
                    @view-wires=${() => {
                        this.navService.navigate('/wires');
                    }}
                    @view-wire-templates=${() => {
                        this.navService.navigate('/wires/templates/view');
                    }}
                ></wire-template-confirm-step>`;
            default:
                throw new Error(`Cannot render step. Invalid step: "${this.activeStep}".`);
        }
    }

    private renderCreditorDetail() {
        return html`<tm-bottom-sheet
            class="z-[101]"
            .open=${this.creditorDetailOpen}
            @close=${() => {
                this.creditorDetailOpen = false;
            }}
        >
            <p slot="header-center">${this.wireIsoLabels.creditor} Detail</p>

            <tm-body .padding=${false}>
                <creditor-data-readonly
                    .creditor=${this.wire?.beneficiary}
                ></creditor-data-readonly>
            </tm-body>
        </tm-bottom-sheet>`;
    }

    private async createWire() {
        this.loading = true;
        this.wireError = undefined;
        try {
            const response = await this.wireService.createWirePayment(this.wire, true);
            this.wire = new Wire(response);
            this.activeStep = Step.Confirm;
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.wireError = error;
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your wire');
            }
        } finally {
            this.loading = false;
        }
    }

    private async updateWireTemplate() {
        this.loading = true;
        this.wireError = undefined;
        try {
            const response = await this.wireService.wiresUpdateTemplate(this.wireTemplate);
            this.wireTemplate = new WireTemplate(response);
            this.activeStep = Step.Confirm;
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.wireError = error;
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your wire');
            }
        } finally {
            this.loading = false;
        }
    }

    private async createWireTemplate() {
        this.loading = true;
        this.wireError = undefined;
        try {
            const response = await this.wireService.wiresCreateTemplate(this.wireTemplate);
            this.wireTemplate = new WireTemplate(response);
            this.activeStep = Step.Confirm;
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.wireError = error;
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your wire');
            }
        } finally {
            this.loading = false;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireTemplateWorkflowContainer;
    }
}
